// MA-395 (engine-truth-2026-09-08.md §E2, secondary gap) — extracted from
// App.jsx's cloud-merge effect so the once-per-(session, student) MA-183
// guard (App.jsx:502-510) can be widened to once-per-(session, student,
// stamps) without inlining the comparison logic in the effect body. Pure and
// unit tested: this predicate is the ONLY thing standing between "device B
// never re-merges even after Fix 2's visibility/focus re-pull lands a fresh
// snapshot" and "a same-device KidSwitcher round-trip re-applies a STALE
// snapshot and clobbers a local edit" (the MA-183 bug this guard exists to
// prevent — see App.jsx's comment at the ref declaration). Keep it pure:
// this file only decides; the caller (App.jsx) owns and mutates the Map.
//
// Old guard (id-only): "has this student id been merged this session?" —
// correct for MA-183 (a same-device switch-away-and-back must not re-apply
// an unchanged snapshot) but ALSO blocks a genuinely newer cloud snapshot
// arriving later in the same session (Fix 2's re-pull), because the id alone
// doesn't change.
//
// New guard (id + stamps): "has THIS EXACT (assigned, suppressed) stamp pair
// already been merged for this student id?" — same protection against a
// re-fire on an unchanged snapshot, but a genuinely newer stamp (a push that
// landed on another device since the last merge) is treated as unseen and
// allowed through.

// Builds the composite key this guard compares. Exported so a caller (or a
// test) can construct the same key from a cloud row without duplicating the
// null-coalescing rule.
//
// MA-1024 (RED r1 S-2, operator D-1018-18) — the row's `mascot_id` is part
// of the key too: the pal has no stamp column, and a focus/visibility
// re-pull that brings another device's newer pal must re-run the merge (the
// pending-sync rule in mascotPullMerge.js keeps an unconfirmed local pick).
// An unchanged snapshot still yields the same key, so the MA-183
// switch-away-and-back case still does not re-merge.
export function stampKey(stamps) {
  const assigned = stamps?.assignedSkillIdsUpdatedAt ?? null;
  const suppressed = stamps?.suppressedSkillIdsUpdatedAt ?? null;
  const mascot = stamps?.mascotId ?? null;
  return `${assigned}|${suppressed}|${mascot}`;
}

// `seenStampByStudentId` — Map<studentId, string> of the last-merged
// composite stamp key per student id. Read-only here; the caller is
// responsible for calling `.set(id, stampKey(stamps))` after a merge it
// decided (based on this function's return value) to actually perform.
//
// `id` — the cloud student's id. Falsy → never merge (mirrors the original
// effect's `if (!cloudStudent) return`).
//
// `stamps` — `{ assignedSkillIdsUpdatedAt, suppressedSkillIdsUpdatedAt, mascotId }`
// off the cloud row RIGHT NOW.
//
// Returns true when this cloud snapshot should be merged:
//  - first sight of this student id this session → true (unchanged from the
//    old id-only guard; still covers "second device after sync" — a fresh
//    session always starts with an empty map).
//  - same student id, the SAME composite stamp already merged → false (the
//    MA-183 KidSwitcher clobber case: nothing left this device between the
//    two fires of this effect, so re-applying the identical snapshot would
//    silently revert a local edit made in between).
//  - same student id, a DIFFERENT composite stamp → true (a genuinely newer
//    push from another device arrived since the last merge).
export function shouldMergeCloudStudent(seenStampByStudentId, id, stamps) {
  if (!id) return false;
  const seen = seenStampByStudentId.get(id);
  if (seen === undefined) return true;
  return seen !== stampKey(stamps);
}

// MA-395 (slice merge-prev-from-storage) — the record the cloud-merge
// effect decides ON. React's in-memory `progress` can be stale: SkillTree's
// toggleAssign/toggleSuppress (and its post-push cloud-mirror update) write
// the child's localStorage record directly and never touch React state, and
// setProgress then saves the merge result over the WHOLE stored record. A
// focus re-merge that decided on the stale React value would silently drop
// a local assign/suppress the cloud never received. So the merge decides on
// the stored record, overlaid on React state:
//  - `storedRecord` a plain object → `{ ...statePrev, ...storedRecord }`
//    (every persisted key wins; a key only React holds survives).
//  - anything else (no record for this child, storage disabled) →
//    `statePrev` unchanged, so a missing key can never wipe React state.
// The caller passes the RAW stored record
// (`loadProgressForChild(id, () => null)`), not the defaults-filled one.
export function progressMergeBase(statePrev, storedRecord) {
  if (!storedRecord || typeof storedRecord !== "object" || Array.isArray(storedRecord)) return statePrev;
  return { ...statePrev, ...storedRecord };
}

// MA-395 (merge-prev-from-storage, RED round 1 S-2) — the merge base, read
// only when the LIVE active child (activeChildIdRef, updated synchronously by
// every switch path) is still the child this merge was scheduled for. If a
// KidSwitcher switch landed between the effect's commit and this updater
// running, `statePrev` already belongs to the new child; overlaying the OLD
// child's whole stored record on it would let the next setProgress save one
// child's record over another's. On a mismatch (or no id) the base is
// `statePrev` — the pre-slice behaviour, never a wider blast radius.
// `loadRaw(id)` must return the RAW stored record or null (App passes
// `(id) => loadProgressForChild(id, () => null)`).
export function readMergeBase(statePrev, { mergeChildId, liveChildId, loadRaw }) {
  if (!mergeChildId || liveChildId !== mergeChildId) return statePrev;
  return progressMergeBase(statePrev, loadRaw(mergeChildId));
}
